import { api } from "../lib/api";
import { JOURNEY_TYPES, type JourneyType } from "../types/journey";
import type {
	JourneyContentLocale,
	JourneyFetchResult,
	JourneyS3File,
} from "../types/journeyS3Schema";
import { JOURNEY_S3_SCHEMA_VERSION_V2 } from "../types/journeyS3Schema";

/**
 * Which journeys have CDN content published per locale. `en` is the implicit
 * fallback for anything missing here, so it needs no entry.
 *
 * Hebrew covers ivf + iui only — egg_freezing has Spanish but no Hebrew yet, so
 * a Hebrew user opening it still falls back to English.
 */
const JOURNEYS_WITH_TRANSLATED_CONTENT = new Map<
	JourneyContentLocale,
	Set<JourneyType>
>([
	["es", new Set<JourneyType>(["ivf", "iui", "egg_freezing"])],
	["he", new Set<JourneyType>(["ivf", "iui"])],
]);

function getBaseLocale(locale: string): string {
	const [baseLocale] = locale.toLowerCase().split("-");
	return baseLocale || "en";
}

export function resolveJourneyContentLocale(
	type: JourneyType,
	locale: string,
): JourneyContentLocale {
	// A Map, not an object: `locale` is caller-supplied, so an object lookup would
	// resolve inherited keys ("constructor", "toString") to non-Set values and blow
	// up on `.has()` instead of falling back to English.
	const baseLocale = getBaseLocale(locale) as JourneyContentLocale;
	const translated = JOURNEYS_WITH_TRANSLATED_CONTENT.get(baseLocale);
	return translated?.has(type) ? baseLocale : "en";
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function parseJourneyEnvelope(value: unknown): JourneyS3File {
	if (!isRecord(value)) {
		throw new Error("Journey CDN response is not an object");
	}

	const { schemaVersion, locale, journeyType, generatedAt, journey } = value;
	if (typeof schemaVersion !== "string") {
		throw new Error("Journey CDN response is missing schemaVersion");
	}
	if (typeof locale !== "string") {
		throw new Error("Journey CDN response is missing locale");
	}
	if (typeof journeyType !== "string") {
		throw new Error("Journey CDN response is missing journeyType");
	}
	if (typeof generatedAt !== "string") {
		throw new Error("Journey CDN response is missing generatedAt");
	}
	if (!isRecord(journey)) {
		throw new Error("Journey CDN response is missing journey");
	}
	if (typeof journey.type !== "string") {
		throw new Error("Journey CDN response journey is missing type");
	}
	if (!Array.isArray(journey.steps)) {
		throw new Error("Journey CDN response journey is missing steps array");
	}

	return value as unknown as JourneyS3File;
}

/**
 * Get the journey for a given treatment type and locale.
 *
 * Fetches through the era-api same-origin proxy `GET /api/journeys/:type/:locale`
 * (era-api PR #145), which fronts the CloudFront CDN. This replaces a raw
 * `fetch()` to the CDN: on native (Capacitor/TestFlight) `CapacitorHttp` patches
 * `fetch` in a way that broke the AbortController-based CDN request, so the
 * journey list silently failed to load. Routing through the API client uses the
 * proper platform-aware transport (and the app's base URL/interceptors).
 *
 * CDN/proxy failures are intentionally surfaced instead of falling back to
 * bundled fixtures, so stale or missing content is visible in development and
 * staging.
 */
export async function getJourney(
	type: JourneyType,
	locale = "en",
): Promise<JourneyFetchResult> {
	const contentLocale = resolveJourneyContentLocale(type, locale);
	try {
		const response = await api.get<unknown>(
			`/api/journeys/${type}/${contentLocale}`,
			{
				// @ts-expect-error - skipAuth is a custom property for public endpoints
				skipAuth: true,
				// Journeys are public and load on onboarding screens — keep the old
				// 5s ceiling instead of waiting for the global API timeout, and never
				// depend on Auth0 token state.
				timeout: 5000,
			},
		);

		const data = parseJourneyEnvelope(response.data);

		if (data.schemaVersion !== JOURNEY_S3_SCHEMA_VERSION_V2) {
			throw new Error(
				`Unsupported schema version: ${data.schemaVersion}. Expected: ${JOURNEY_S3_SCHEMA_VERSION_V2}`,
			);
		}

		if (data.journeyType !== type || data.journey.type !== type) {
			throw new Error(
				`Journey type mismatch: requested ${type}, received envelope ${data.journeyType} and journey ${data.journey.type}`,
			);
		}

		if (data.locale !== contentLocale) {
			throw new Error(
				`Journey locale mismatch: requested ${contentLocale}, received ${data.locale}`,
			);
		}

		return { journey: data.journey, source: "cdn", contentLocale };
	} catch (error) {
		throw new Error(`Failed to load journey: ${type}/${contentLocale}`, {
			cause: error,
		});
	}
}

/**
 * Get the list of all available journey types.
 * Future: backed by `GET /api/journey-types`.
 */
export async function getJourneyTypes(): Promise<JourneyType[]> {
	return [...JOURNEY_TYPES];
}
